import MetricBox from "components-for/display/metric-box";
import useInteractionsCounts from "hooks-for/interactions/use-interactions-counts";
import { AgentStateType, InteractionState } from "@edgetier/watchtower-types";
import { aggregateValues } from "./available-agents-capacity-metric.utilities";
import { FunctionComponent, memo, useMemo } from "react";
import { IProps } from "./available-agents-capacity-metric.types";
import { LIVE_POLL_INTERVAL_MILLISECONDS } from "constants/pages/live";
import { useInteractionQueryParameters } from "hooks-for/filters/use-interaction-query-parameters";
import "./available-agents-capacity-metric.scss";

/**
 * Get the available agents and calculate a summary value based on their activity (e.g. average capacity).
 * @param props.aggregate Function to summaries the count values.
 * @param otherProps      Props for the metric component.
 * @returns               Metric icon and value.
 */
const AvailableAgentsCapacityMetric: FunctionComponent<IProps> = ({ aggregate, isLoading, ...otherProps }) => {
    // Get all agents that aren't offline.
    const queryOptions = { refetchInterval: LIVE_POLL_INTERVAL_MILLISECONDS };

    // Count interactions being handled by agents that are currently available.
    const interactionState = [InteractionState.Handling];
    const agentStateType = [AgentStateType.Available];
    const interactionQueryParameters = useInteractionQueryParameters({ defaults: { interactionState } });
    const countsQueryParameters = { ...interactionQueryParameters, agentStateType, groupBy: "agent_id" };

    // Calculate the total, mean etc.
    const { data: interactionsCounts, isLoading: isInteractionsCountsLoading } = useInteractionsCounts(
        countsQueryParameters,
        queryOptions
    );

    // Summarise the count values (maximum, mean etc.)
    const value = useMemo(() => aggregateValues(interactionsCounts, aggregate), [aggregate, interactionsCounts]);

    return (
        <MetricBox
            isLoading={isInteractionsCountsLoading || isLoading}
            value={value}
            options={{ maximumFractionDigits: 1 }}
            {...otherProps}
        />
    );
};

export default memo(AvailableAgentsCapacityMetric);
